/**
 * `/design-room` — public landing for the Design Room feature (PLAN_DESIGN_ROOM_TIM.md §2 F4).
 * Thesis + copy sourced from RISET_DESIGN_ROOM_TIM.md §5.1–5.3, §6. Landing-styled (`.mk`, light-only, no
 * shadcn primitives in here), same pattern as `studio.tsx` / `campus.tsx`. The room product itself (creating
 * a room, joining as a guest) is F1/F2 work in a parallel session — every CTA here routes to `/sign-in`,
 * `/pricing` or another public page, never to an unbuilt in-app route.
 */
import type { ReactNode } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowRight, GraduationCap, Lock, Rocket, ShieldCheck, Sparkles, Users } from 'lucide-react'
import { Container, CtaPair, MkAnchor, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/design-room')({
  head: () => ({
    meta: [
      { title: m.dr_meta_title() },
      { name: 'description', content: m.dr_meta_desc() },
      { property: 'og:title', content: m.dr_meta_title() },
      { property: 'og:description', content: m.dr_meta_desc() },
      { property: 'og:type', content: 'website' },
    ],
  }),
  component: DesignRoomLanding,
})

// ---------------------------------------------------------------- hero

function Hero() {
  return (
    <section className="pt-16 pb-10 lg:pt-24">
      <Container className="flex flex-col items-center gap-6 text-center">
        <Overline>{m.dr_hero_overline()}</Overline>
        <h1 className="max-w-[20ch]">{m.dr_hero_title()}</h1>
        <p className="mk-lead max-w-[60ch]">{m.dr_hero_lead()}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-3">
          <MkLink to="/sign-in" search={{ redirect: '/home' }}>
            {m.dr_hero_cta_primary()}
          </MkLink>
          <MkAnchor href="#cara" variant="secondary" arrow>
            {m.dr_hero_cta_secondary()}
          </MkAnchor>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- six steps

type StepRow = { name: string; artifact: string; rule: string; ai: string }

function Steps() {
  const rows: StepRow[] = [
    { name: m.dr_step1_name(), artifact: m.dr_step1_artifact(), rule: m.dr_step1_rule(), ai: m.dr_step1_ai() },
    { name: m.dr_step2_name(), artifact: m.dr_step2_artifact(), rule: m.dr_step2_rule(), ai: m.dr_step2_ai() },
    { name: m.dr_step3_name(), artifact: m.dr_step3_artifact(), rule: m.dr_step3_rule(), ai: m.dr_step3_ai() },
    { name: m.dr_step4_name(), artifact: m.dr_step4_artifact(), rule: m.dr_step4_rule(), ai: m.dr_step4_ai() },
    { name: m.dr_step5_name(), artifact: m.dr_step5_artifact(), rule: m.dr_step5_rule(), ai: m.dr_step5_ai() },
    { name: m.dr_step6_name(), artifact: m.dr_step6_artifact(), rule: m.dr_step6_rule(), ai: m.dr_step6_ai() },
  ]
  return (
    <section id="cara" className="scroll-mt-20 py-14 lg:py-20">
      <Container className="flex flex-col gap-10">
        <SectionHead align="left" overline={m.dr_steps_overline()} title={m.dr_steps_title()} lead={m.dr_steps_lead()} />
        <div className="mk-card-white overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left text-[14px]">
            <thead>
              <tr className="border-b border-[var(--mk-border)] text-sm text-[var(--mk-fg-muted)]">
                <th scope="col" className="w-[16%] px-5 py-4 font-medium">
                  {m.dr_steps_col_step()}
                </th>
                <th scope="col" className="w-[28%] px-4 py-4 font-medium">
                  {m.dr_steps_col_artifact()}
                </th>
                <th scope="col" className="w-[28%] px-4 py-4 font-medium">
                  {m.dr_steps_col_rule()}
                </th>
                <th scope="col" className="px-4 py-4 font-medium">
                  {m.dr_steps_col_ai()}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-b border-[var(--mk-border)] align-top last:border-b-0">
                  <th scope="row" className="px-5 py-4 font-medium text-[var(--mk-fg)]">
                    {r.name}
                  </th>
                  <td className="px-4 py-4 leading-relaxed text-[var(--mk-fg-muted)]">{r.artifact}</td>
                  <td className="px-4 py-4 leading-relaxed text-[var(--mk-fg-muted)]">{r.rule}</td>
                  <td className="px-4 py-4 leading-relaxed text-[var(--mk-fg-muted)]">{r.ai}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- who it's for

function WhoCard({ icon: I, title, desc, price, link }: { icon: typeof Users; title: string; desc: string; price?: string; link?: ReactNode }) {
  return (
    <article className="mk-card-white flex flex-col gap-3 p-6">
      <span className="flex size-9 items-center justify-center rounded-lg border border-[var(--mk-border)] bg-white text-[var(--mk-fg)]">
        <I className="size-4" aria-hidden />
      </span>
      <h3 className="text-lg!">{title}</h3>
      <p className="mk-muted flex-1 text-sm leading-relaxed">{desc}</p>
      {price ? <p className="text-sm font-medium text-[var(--mk-fg)]">{price}</p> : null}
      {link}
    </article>
  )
}

const whoLinkCls = 'mt-1 inline-flex items-center gap-1.5 text-[14px] font-medium text-[var(--mk-fg)] hover:text-[var(--mk-fg-muted)]'

function Who() {
  return (
    <section className="scroll-mt-20 bg-[var(--mk-bg-muted)] py-16 lg:py-24">
      <Container className="flex flex-col gap-12">
        <SectionHead overline={m.dr_who_overline()} title={m.dr_who_title()} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <WhoCard icon={Users} title={m.dr_who1_title()} desc={m.dr_who1_desc()} />
          <WhoCard
            icon={Rocket}
            title={m.dr_who2_title()}
            desc={m.dr_who2_desc()}
            link={
              <Link to="/studio" className={whoLinkCls}>
                {m.dr_who2_link()}
                <ArrowRight className="size-3.5" aria-hidden />
              </Link>
            }
          />
          <WhoCard
            icon={GraduationCap}
            title={m.dr_who3_title()}
            desc={m.dr_who3_desc()}
            link={
              <Link to="/campus" className={whoLinkCls}>
                {m.dr_who3_link()}
                <ArrowRight className="size-3.5" aria-hidden />
              </Link>
            }
          />
          <WhoCard
            icon={Sparkles}
            title={m.dr_who4_title()}
            desc={m.dr_who4_desc()}
            price={m.dr_who4_price()}
            link={
              <Link to="/pricing" search={{ voucher: undefined }} className={whoLinkCls}>
                {m.dr_who4_link()}
                <ArrowRight className="size-3.5" aria-hidden />
              </Link>
            }
          />
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- why not a whiteboard

function WhyNotWhiteboard() {
  const points = [m.dr_why_point1(), m.dr_why_point2(), m.dr_why_point3()]
  return (
    <section className="py-16 lg:py-24">
      <Container className="flex flex-col gap-10">
        <SectionHead align="left" overline={m.dr_why_overline()} title={m.dr_why_title()} />
        <ul className="grid gap-6 md:grid-cols-3">
          {points.map((p) => (
            <li key={p} className="mk-card-white p-5 text-[14px] leading-relaxed text-[var(--mk-fg-muted)]">
              {p}
            </li>
          ))}
        </ul>
        <p className="mk-lead max-w-[60ch]">
          {m.dr_why_quote_pre()}{' '}
          <Link to="/compare/$slug" params={{ slug: 'miro' }} className="text-[var(--mk-fg)] underline decoration-[var(--mk-border)] underline-offset-4 hover:decoration-[var(--mk-fg)]">
            Miro
          </Link>
          {m.dr_why_quote_mid()}{' '}
          <Link to="/integrations" className="text-[var(--mk-fg)] underline decoration-[var(--mk-border)] underline-offset-4 hover:decoration-[var(--mk-fg)]">
            Bystep
          </Link>
          {m.dr_why_quote_end()}
        </p>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- Proof-of-Why mock

function ProofOfWhy() {
  return (
    <section className="mk-dark-section py-20 lg:py-28">
      <Container className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="flex flex-col items-start gap-5">
          <Overline className="text-[#7d9bff]!">{m.dr_proof_overline()}</Overline>
          <h2 className="max-w-[18ch]">{m.dr_proof_title()}</h2>
          <p className="max-w-[52ch] text-[16px] leading-relaxed text-[var(--mk-dark-fg-muted)]">{m.dr_proof_lead()}</p>
        </div>
        <div className="rounded-xl border border-[var(--mk-dark-border)] bg-[var(--mk-dark-card)] p-6">
          <div className="flex flex-col gap-4 font-mono text-[13px]">
            <div className="flex items-start gap-3">
              <span className="shrink-0 rounded-md bg-[#7d9bff]/15 px-2 py-1 text-[#b8c8ff]">{m.dr_proof_h1_label()}</span>
              <span className="text-[var(--mk-dark-fg)]">{m.dr_proof_h1_text()}</span>
            </div>
            <div className="pl-6 text-[var(--mk-dark-fg-muted)]">↓ validates</div>
            <div className="flex items-start gap-3">
              <span className="shrink-0 rounded-md bg-white/5 px-2 py-1 text-[var(--mk-dark-fg)]">{m.dr_proof_ac_label()}</span>
              <span className="text-[var(--mk-dark-fg)]">{m.dr_proof_ac_text()}</span>
            </div>
            <div className="pl-6 text-[var(--mk-dark-fg-muted)]">↓ {m.dr_proof_cmd()}</div>
            <div className="flex items-center gap-2 rounded-md border border-status-done/40 bg-status-done/10 px-3 py-2">
              <ShieldCheck className="size-4 shrink-0 text-status-done" aria-hidden />
              <span className="text-[var(--mk-dark-fg)]">
                {m.dr_proof_status_label()}: {m.dr_proof_status_value()}
              </span>
            </div>
          </div>
          <p className="mt-4 text-[13px] leading-relaxed text-[var(--mk-dark-fg-muted)]">{m.dr_proof_cmd_caption()}</p>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- FAQ

function Faq() {
  const items = [
    { q: m.dr_faq_q1(), a: m.dr_faq_a1() },
    { q: m.dr_faq_q2(), a: m.dr_faq_a2() },
    { q: m.dr_faq_q3(), a: m.dr_faq_a3() },
    { q: m.dr_faq_q4(), a: m.dr_faq_a4() },
    { q: m.dr_faq_q5(), a: m.dr_faq_a5() },
  ]
  return (
    <section className="py-16 lg:py-24">
      <Container className="flex flex-col gap-10">
        <SectionHead overline={m.dr_faq_overline()} title={m.dr_faq_title()} />
        <dl className="mx-auto flex w-full max-w-3xl flex-col divide-y divide-[var(--mk-border)] border-y border-[var(--mk-border)]">
          {items.map(({ q, a }) => (
            <div key={q} className="flex flex-col gap-2 py-5">
              <dt className="flex items-start gap-2 text-[15px] font-medium text-[var(--mk-fg)]">
                <Lock className="mt-0.5 size-3.5 shrink-0 text-[var(--mk-fg-faint)]" aria-hidden />
                {q}
              </dt>
              <dd className="pl-5.5 text-[14px] leading-relaxed text-[var(--mk-fg-muted)]">{a}</dd>
            </div>
          ))}
        </dl>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- final CTA

function FinalCta() {
  return (
    <section className="py-24 lg:py-32">
      <Container>
        <SectionHead
          title={m.dr_final_title()}
          lead={m.dr_final_lead()}
          actions={<CtaPair primary={m.dr_final_cta_primary()} secondary={m.dr_final_cta_secondary()} secondaryTo="/consulting" />}
        />
      </Container>
    </section>
  )
}

function DesignRoomLanding() {
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <Hero />
        <Steps />
        <Who />
        <WhyNotWhiteboard />
        <ProofOfWhy />
        <Faq />
        <FinalCta />
      </main>
      <MkFooter />
    </div>
  )
}
